package com.narbase.letsgo.web.views.mobileUpdate

import com.narbase.kunafa.core.components.*
import com.narbase.kunafa.core.components.layout.LinearLayout
import com.narbase.kunafa.core.css.*
import com.narbase.kunafa.core.dimensions.dependent.matchParent
import com.narbase.kunafa.core.dimensions.dependent.wrapContent
import com.narbase.kunafa.core.dimensions.percent
import com.narbase.kunafa.core.dimensions.px
import com.narbase.kunafa.core.dimensions.vw
import com.narbase.kunafa.core.drawable.Color
import com.narbase.kunafa.core.lifecycle.LifecycleOwner
import com.narbase.letsgo.web.common.AppColors
import com.narbase.letsgo.web.dto.config.MobileUpdateConfigDto
import com.narbase.letsgo.web.dto.config.MobileUpdatePlatformConfigDto
import com.narbase.letsgo.web.network.makeNotVisible
import com.narbase.letsgo.web.network.makeVisible
import com.narbase.letsgo.web.theme.theme
import com.narbase.letsgo.web.translations.localized
import com.narbase.letsgo.web.utils.horizontalFiller
import com.narbase.letsgo.web.utils.scrollable.scrollable
import com.narbase.letsgo.web.utils.verticalFiller
import com.narbase.letsgo.web.utils.views.*
import com.narbase.letsgo.web.utils.views.customViews.CustomCheckBox
import com.narbase.letsgo.web.utils.views.customViews.customCheckBox

class MobileUpdateManagementComponent : Component() {

    private val viewModel = MobileUpdateManagementViewModel()
    private val popup by lazy { popUpDialog() }

    override fun onViewMounted(lifecycleOwner: LifecycleOwner) {
        super.onViewMounted(lifecycleOwner)
        viewModel.getConfig()
    }

    override fun View?.getView() = view {
        id = "mobileUpdateManagementRootView"

        style {
            matchParentDimensions
        }
        scrollable {
            style {
                matchParentDimensions
            }

            verticalLayout {
                style {
                    width = matchParent
                    height = wrapContent
                    padding = 32.px
                }

                horizontalLayout {
                    style {
                        width = matchParent
                    }
                    textView {
                        text = "Over The Air Update Settings".localized()
                        style {
                            width = wrapContent
                            fontSize = 20.px
                            fontWeight = "bold"
                        }
                    }
                    horizontalFiller()
                }
                verticalFiller(20)

                horizontalLayout {
                    style {
                        width = 60.percent
                    }
                    horizontalFiller(20)
                    verticalLayout {
                        withLoadingAndError(viewModel.getConfigUiState, onRetryClicked = {
                            viewModel.getConfig()
                        }, onLoaded = {
                            clearAllChildren()
                            val config = viewModel.config
                            theme.label(this, "Enabled".localized(), false)
                            theme.text(this) { text = (config?.isEnabled ?: false).toString() }
                            theme.label(this, "Ignore Last Minor Version".localized(), false)
                            theme.text(this) { text = (config?.ignoreLastMinorVersion ?: false).toString() }
                            theme.label(this, "Stats URL".localized(), false)
                            theme.text(this) { text = config?.statsUrl ?: "-" }

                            verticalFiller(16)
                            platformSummary("Android".localized(), config?.android)
                            verticalFiller(16)
                            platformSummary("iOS".localized(), config?.ios)
                        })
                    }
                    horizontalFiller()

                    editConfigButton()
                }
            }
        }
    }

    private fun LinearLayout.platformSummary(title: String, dto: MobileUpdatePlatformConfigDto?) = verticalLayout {
        matchParentWidth()
        theme.subTitleFit(this, title)
        theme.label(this, "Force Update Required".localized(), false)
        theme.text(this) { text = (dto?.isForceUpdateRequiredEnabled ?: false).toString() }
        theme.label(this, "Minimum Version For Force Update".localized(), false)
        theme.text(this) { text = dto?.minVersionForForceUpdate ?: "-" }
        theme.label(this, "Update In Background".localized(), false)
        theme.text(this) { text = (dto?.isForcedUpdateInBackground ?: false).toString() }
        theme.label(this, "Update Optional".localized(), false)
        theme.text(this) { text = (dto?.isForcedUpdateOptional ?: false).toString() }
    }

    private fun LinearLayout.editConfigButton() {
        theme.mainButton(this) {
            id = "editMobileUpdateConfig"
            onClick = {
                upsertDialog(viewModel.config)
            }
            text = "Edit".localized()
        }
    }

    private var isEnabledCheckBox: CustomCheckBox? = null
    private var ignoreLastMinorVersionCheckBox: CustomCheckBox? = null

    private var androidForceUpdateCheckBox: CustomCheckBox? = null
    private var androidMinVersionInput: TextInput? = null
    private var androidInBackgroundCheckBox: CustomCheckBox? = null
    private var androidOptionalCheckBox: CustomCheckBox? = null

    private var iosForceUpdateCheckBox: CustomCheckBox? = null
    private var iosMinVersionInput: TextInput? = null
    private var iosInBackgroundCheckBox: CustomCheckBox? = null
    private var iosOptionalCheckBox: CustomCheckBox? = null
    private var statsUrlInput: TextInput? = null

    private fun upsertDialog(dto: MobileUpdateConfigDto?) {
        viewModel.setUpsertUiStateToNull()
        theme.showDialog(popup) {

            verticalLayout {
                style {
                    height = wrapContent
                    width = 70.vw
                    backgroundColor = Color.white
                    padding = 20.px
                    borderRadius = 8.px
                }
                textView {
                    style {
                        fontWeight = "bold"
                        marginBottom = 8.px
                        fontSize = 16.px
                    }
                    text = "Edit Over The Air Update Settings".localized()
                }

                isEnabledCheckBox = customCheckBox("Enabled".localized(), isSelected = dto?.isEnabled ?: true)
                ignoreLastMinorVersionCheckBox =
                    customCheckBox("Ignore Last Minor Version".localized(), isSelected = dto?.ignoreLastMinorVersion ?: false)

                statsUrlInput = theme.labeledTextInput(this, "Stats URL".localized(), false)
                statsUrlInput?.text = dto?.statsUrl ?: ""
                statsUrlInput?.placeholder = "/api/v1/public/post_ota_stats_log"

                verticalFiller(16)
                platformEditFields("Android".localized(), dto?.android, isAndroid = true)
                verticalFiller(16)
                platformEditFields("iOS".localized(), dto?.ios, isAndroid = false)

                verticalFiller(12)
                val errorText = textView {
                    style {
                        marginBottom = 8.px
                        fontSize = 14.px
                        color = AppColors.redLight
                    }
                    isVisible = false
                    text = "Please enter valid fields values".localized()
                }
                verticalFiller(10)

                it.bottomBar = {
                    horizontalLayout {
                        style {
                            width = matchParent
                            height = wrapContent
                            justifyContent = JustifyContent.End
                            marginTop = 10.px
                        }

                        theme.mainButton(this) {
                            text = "Save".localized()
                            onClick = {
                                onSaveButtonClicked(errorText)
                            }

                            withLoadingAndError(viewModel.upsertUiState, onRetryClicked = {
                                onSaveButtonClicked(errorText)
                            }, onLoaded = {
                                popup.dismissDialog()
                                viewModel.getConfig()
                            })
                        }
                    }
                }
            }
        }
    }

    private fun LinearLayout.platformEditFields(title: String, dto: MobileUpdatePlatformConfigDto?, isAndroid: Boolean) =
        verticalLayout {
            matchParentWidth()
            theme.subTitleFit(this, title)

            val forceUpdateCheckBox =
                customCheckBox("Force Update Required".localized(), isSelected = dto?.isForceUpdateRequiredEnabled ?: false)
            val minVersionInput = theme.labeledTextInput(this, "Minimum Version For Force Update".localized(), true)
            minVersionInput.text = dto?.minVersionForForceUpdate ?: ""
            minVersionInput.placeholder = "eg. 1.0.0"

            val inBackgroundCheckBox =
                customCheckBox("Update In Background".localized(), isSelected = dto?.isForcedUpdateInBackground ?: true)
            val optionalCheckBox =
                customCheckBox("Update Optional".localized(), isSelected = dto?.isForcedUpdateOptional ?: true)

            if (isAndroid) {
                androidForceUpdateCheckBox = forceUpdateCheckBox
                androidMinVersionInput = minVersionInput
                androidInBackgroundCheckBox = inBackgroundCheckBox
                androidOptionalCheckBox = optionalCheckBox
            } else {
                iosForceUpdateCheckBox = forceUpdateCheckBox
                iosMinVersionInput = minVersionInput
                iosInBackgroundCheckBox = inBackgroundCheckBox
                iosOptionalCheckBox = optionalCheckBox
            }
        }

    private fun onSaveButtonClicked(errorText: TextView) {
        val isEnabled = isEnabledCheckBox?.isSelected ?: return
        val ignoreLastMinorVersion = ignoreLastMinorVersionCheckBox?.isSelected ?: return

        val androidMinVersion = androidMinVersionInput?.text?.trim()
        val iosMinVersion = iosMinVersionInput?.text?.trim()

        if (androidMinVersion.isNullOrBlank()) {
            makeVisible(errorText)
            errorText.text = "Please enter a valid minimum version for Android force update".localized()
            return
        }
        if (iosMinVersion.isNullOrBlank()) {
            makeVisible(errorText)
            errorText.text = "Please enter a valid minimum version for iOS force update".localized()
            return
        }

        makeNotVisible(errorText)

        val androidConfig = MobileUpdatePlatformConfigDto(
            isForceUpdateRequiredEnabled = androidForceUpdateCheckBox?.isSelected ?: false,
            minVersionForForceUpdate = androidMinVersion,
            isForcedUpdateInBackground = androidInBackgroundCheckBox?.isSelected ?: true,
            isForcedUpdateOptional = androidOptionalCheckBox?.isSelected ?: true,
        )
        val iosConfig = MobileUpdatePlatformConfigDto(
            isForceUpdateRequiredEnabled = iosForceUpdateCheckBox?.isSelected ?: false,
            minVersionForForceUpdate = iosMinVersion,
            isForcedUpdateInBackground = iosInBackgroundCheckBox?.isSelected ?: true,
            isForcedUpdateOptional = iosOptionalCheckBox?.isSelected ?: true,
        )

        val statsUrl = statsUrlInput?.text?.trim()?.ifBlank { null }

        viewModel.upsertConfig(
            MobileUpdateConfigDto(
                isEnabled = isEnabled,
                ignoreLastMinorVersion = ignoreLastMinorVersion,
                android = androidConfig,
                ios = iosConfig,
                statsUrl = statsUrl,
            )
        )
    }
}
